// release-resume-deploy-state-precheck.ts
//
// Code review P3 M1 (2026-10-01): what the mission already knows about deploys
// is checked BEFORE any G3 is withdrawn for another candidate (CXR-03,
// release-g3-rerequest.ts) and before any review/push of a new one. A deploy
// of candidate A that started and never reported back may have put A live;
// withdrawing A's G3 first let candidate B be reviewed and pushed over the MR
// while nobody knew. Follow-up review Low 2: whatever the G3 status — a
// redirect may already have invalidated A's G3. Split out of
// supervisor-release-flow-resume.ts (200-line budget).
//   - a receipt written by an older CLI ⇒ release_unverified_legacy (review L1);
//   - any deploy intent of the mission the journal cannot settle ⇒
//     release_deploy_outcome_unknown (Luke 30/09), gate left exactly as it is.
import { readReleaseReceiptState } from "./release-deploy-completion-receipt.js";
import { assessDeployOutcome } from "./release-deploy-outcome-assessment.js";
import {
  computeReleaseDeployHash,
  computeReleaseManifestHash,
  type ReleaseConfig,
} from "./release-manifest-hash.js";
import { LEGACY_RECEIPT_REASON } from "./supervisor-release-approved-g3-leg.js";
import type { ResumeReleaseFlowInput } from "./supervisor-release-flow-resume-input.js";
import type { ReleaseFlowResumeOutcome } from "./supervisor-release-flow-resume-outcome.js";

/** Mission HEAD reader: `readCandidateSha` when wired, else the candidate half of `readCandidateAndBaseSha`. */
export function candidateShaReader(
  input: Pick<ResumeReleaseFlowInput, "readCandidateSha" | "readCandidateAndBaseSha">,
): () => Promise<string> {
  return (
    input.readCandidateSha ?? (async () => (await input.readCandidateAndBaseSha()).candidateSha)
  );
}

/** The stop outcome, or `undefined` when the resume may go on (nothing unsettled). */
export async function precheckDeployStateBeforeRelease(
  input: Pick<ResumeReleaseFlowInput, "store" | "controlRoot"> & {
    release: ReleaseConfig;
    readCandidateSha: () => Promise<string>;
  },
): Promise<ReleaseFlowResumeOutcome | undefined> {
  const candidateSha = await input.readCandidateSha();
  const releaseDeployHash = computeReleaseDeployHash(candidateSha, input.release);
  const receipts = await readReleaseReceiptState(input.controlRoot, {
    candidateSha,
    releaseManifestHash: computeReleaseManifestHash(candidateSha, input.release),
    releaseDeployHash,
  });
  if (receipts.legacy) return { kind: "release_unverified_legacy", reason: LEGACY_RECEIPT_REASON };
  const deployOutcome = await assessDeployOutcome({
    state: input.store.state,
    receipts,
    candidateSha,
    releaseDeployHash,
  });
  if (deployOutcome.kind === "unknown")
    return { kind: "release_deploy_outcome_unknown", reason: deployOutcome.reason };
  return undefined;
}
